/** Countries for display currency (course prices are stored in KES). */
export interface CurrencyCountry {
  iso2: string;
  name: string;
  currency: string;
  locale: string;
}

/** Kenya first; remaining sorted by name. */
export const CURRENCY_COUNTRIES: CurrencyCountry[] = [
  { iso2: 'KE', name: 'Kenya', currency: 'KES', locale: 'en-KE' },
  { iso2: 'UG', name: 'Uganda', currency: 'UGX', locale: 'en-UG' },
  { iso2: 'TZ', name: 'Tanzania', currency: 'TZS', locale: 'en-TZ' },
  { iso2: 'RW', name: 'Rwanda', currency: 'RWF', locale: 'en-RW' },
  { iso2: 'ET', name: 'Ethiopia', currency: 'ETB', locale: 'en-ET' },
  { iso2: 'ZA', name: 'South Africa', currency: 'ZAR', locale: 'en-ZA' },
  { iso2: 'NG', name: 'Nigeria', currency: 'NGN', locale: 'en-NG' },
  { iso2: 'GH', name: 'Ghana', currency: 'GHS', locale: 'en-GH' },
  { iso2: 'EG', name: 'Egypt', currency: 'EGP', locale: 'ar-EG' },
  { iso2: 'MA', name: 'Morocco', currency: 'MAD', locale: 'fr-MA' },
  { iso2: 'US', name: 'United States', currency: 'USD', locale: 'en-US' },
  { iso2: 'GB', name: 'United Kingdom', currency: 'GBP', locale: 'en-GB' },
  { iso2: 'DE', name: 'Germany', currency: 'EUR', locale: 'de-DE' },
  { iso2: 'FR', name: 'France', currency: 'EUR', locale: 'fr-FR' },
  { iso2: 'NL', name: 'Netherlands', currency: 'EUR', locale: 'nl-NL' },
  { iso2: 'IT', name: 'Italy', currency: 'EUR', locale: 'it-IT' },
  { iso2: 'ES', name: 'Spain', currency: 'EUR', locale: 'es-ES' },
  { iso2: 'IE', name: 'Ireland', currency: 'EUR', locale: 'en-IE' },
  { iso2: 'BE', name: 'Belgium', currency: 'EUR', locale: 'fr-BE' },
  { iso2: 'AT', name: 'Austria', currency: 'EUR', locale: 'de-AT' },
  { iso2: 'PT', name: 'Portugal', currency: 'EUR', locale: 'pt-PT' },
  { iso2: 'SE', name: 'Sweden', currency: 'SEK', locale: 'sv-SE' },
  { iso2: 'NO', name: 'Norway', currency: 'NOK', locale: 'nb-NO' },
  { iso2: 'DK', name: 'Denmark', currency: 'DKK', locale: 'da-DK' },
  { iso2: 'CH', name: 'Switzerland', currency: 'CHF', locale: 'de-CH' },
  { iso2: 'PL', name: 'Poland', currency: 'PLN', locale: 'pl-PL' },
  { iso2: 'IN', name: 'India', currency: 'INR', locale: 'en-IN' },
  { iso2: 'AE', name: 'United Arab Emirates', currency: 'AED', locale: 'ar-AE' },
  { iso2: 'SA', name: 'Saudi Arabia', currency: 'SAR', locale: 'ar-SA' },
  { iso2: 'AU', name: 'Australia', currency: 'AUD', locale: 'en-AU' },
  { iso2: 'NZ', name: 'New Zealand', currency: 'NZD', locale: 'en-NZ' },
  { iso2: 'CA', name: 'Canada', currency: 'CAD', locale: 'en-CA' },
  { iso2: 'JP', name: 'Japan', currency: 'JPY', locale: 'ja-JP' },
  { iso2: 'KR', name: 'South Korea', currency: 'KRW', locale: 'ko-KR' },
  { iso2: 'CN', name: 'China', currency: 'CNY', locale: 'zh-CN' },
  { iso2: 'SG', name: 'Singapore', currency: 'SGD', locale: 'en-SG' },
  { iso2: 'MY', name: 'Malaysia', currency: 'MYR', locale: 'ms-MY' },
  { iso2: 'BW', name: 'Botswana', currency: 'BWP', locale: 'en-BW' },
  { iso2: 'ZM', name: 'Zambia', currency: 'ZMW', locale: 'en-ZM' },
  { iso2: 'ZW', name: 'Zimbabwe', currency: 'ZWL', locale: 'en-ZW' },
];

/** Approximate units of target currency per 1 KES (fallback when live rates are unavailable). */
export const FALLBACK_RATES_FROM_KES: Record<string, number> = {
  KES: 1,
  UGX: 28.5,
  TZS: 18.8,
  RWF: 10.2,
  ETB: 0.95,
  ZAR: 0.15,
  NGN: 11.2,
  GHS: 0.095,
  EGP: 0.38,
  MAD: 0.075,
  USD: 0.0077,
  GBP: 0.0061,
  EUR: 0.0071,
  SEK: 0.082,
  NOK: 0.083,
  DKK: 0.053,
  CHF: 0.0068,
  PLN: 0.03,
  INR: 0.68,
  AED: 0.028,
  SAR: 0.029,
  AUD: 0.012,
  NZD: 0.013,
  CAD: 0.011,
  JPY: 1.18,
  KRW: 10.8,
  CNY: 0.056,
  SGD: 0.01,
  MYR: 0.034,
  BWP: 0.11,
  ZMW: 0.2,
  ZWL: 0.025,
};

export function getCountryByIso2(iso2: string): CurrencyCountry | undefined {
  return CURRENCY_COUNTRIES.find((c) => c.iso2 === iso2);
}

const STORAGE_KEY = 'homeland_currency_iso2';

export function readStoredCurrencyIso2(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && getCountryByIso2(v) ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredCurrencyIso2(iso2: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, iso2);
  } catch {
    /* ignore */
  }
}
